import { useContext, useState } from "react";
import { CodedProperty, getById, getByIds, NamedProperty, Optional, ValidationResult } from "sprint-common-web";

import { AirlinesDataContext } from "../../../../common/data/lookup-data-providers/AirlinesDataProvider";
import {
    Distributor,
    DistributorsDataContext
} from "../../../../common/data/lookup-data-providers/DistributorsDataProvider";
import { VersionTypesDataContext } from "../../../../common/data/lookup-data-providers/VersionTypesDataProvider";
import { ContentSource } from "../../../../common/types/ContentSource";
import { useAssetsManageAncillaryApi } from "../../api/useAssetsManageAncillaryApi";
import { ManageAncillaryContext } from "../../context/ManageAncillaryContext";
import { AncillaryItem } from "../AncillaryItem";
import { CreateAncillaryFormInputs } from "./CreateAncillaryFormInputs";

export const useCreateAncillaryForm = () => {
    const { validateCreateAncillary, createAncillary } = useAssetsManageAncillaryApi();

    const [validationResult, setValidationResult] = useState<ValidationResult>();
    const { airlines } = useContext(AirlinesDataContext);
    const { nonTrailerVersionTypes: versionTypes } = useContext(VersionTypesDataContext);
    const { getDistributorById } = useContext(DistributorsDataContext);
    const { ancillaryContentType } = useContext(ManageAncillaryContext);

    const createNewAncillary = async (inputs: CreateAncillaryFormInputs): Promise<Optional<AncillaryItem>> => {
        const subtype = ancillaryContentType;
        const { title, airlineIds, versionTypeId, distributorId } = inputs;

        const validationResponse = await validateCreateAncillary({ versionTypeId });

        setValidationResult(validationResponse);

        if (!validationResponse.isValid) {
            return;
        }

        const createAncillaryResponse = await createAncillary({
            title,
            airlineIds,
            versionTypeId,
            distributorId,
            subtype
        });

        return mapToAncillaryItem({
            id: createAncillaryResponse.entityId,
            title,
            airlines: getByIds(airlines, airlineIds),
            versionType: getById(versionTypes, versionTypeId),
            distributor: await getDistributorById(distributorId)
        });
    };

    return {
        airlines,
        versionTypes,
        createNewAncillary,
        validationResult,
        setValidationResult
    };
};

const mapToAncillaryItem = (ancillaryParams: {
    id: string;
    title: string;
    airlines: CodedProperty[];
    versionType: NamedProperty;
    distributor: Distributor;
}) => {
    return {
        id: ancillaryParams.id,
        title: ancillaryParams.title,
        orphaned: true,
        source: ContentSource.Sprint,
        versionType: ancillaryParams.versionType,
        airlines: ancillaryParams.airlines,
        distributor: ancillaryParams.distributor
    };
};
